import React, { useEffect, useState } from "react";
import Header from "src/components/app/layout/Header";
import Layout from "src/components/app/layout/Layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { getOrdinal } from "src/helpers/journey";
import { Calendar, SquarePen } from "lucide-react";
import Button from "src/components/app/Button";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as locationAction from "src/redux/slices/locationSlice";
import { AddressListResponse } from "src/types/location.types";
import * as journeyActions from "src/redux/slices/journeySlice";
import { constants } from "src/constants/common";
import { toast } from "sonner";
import { DATE_FORMAT_STRING } from "src/constants/dataTimeFormats";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";

const ClaimDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { nextCycle } = location.state || {};
  const supportProgramKey = params["support-program-key"];
  const { patient } = useAppSelector((state) => state.auth);
  const addressList = useAppSelector((state) => state.location.getAddressList);
  const [newDeliveryAddress, setNewDeliveryAddress] =
    useState<AddressListResponse | null>(null);

  const addresses = addressList?.data || [];

  const getAddressList = () => {
    new Promise((resolve, reject) => {
      dispatch(
        locationAction.getAddressListRequest({ resolve, reject, payload: {} })
      );
    });
  };

  useEffect(() => {
    // Event: CLAIM_DETAILS_SCREEN_VIEWED
    analytics.pageView(SCREEN_NAMES.CLAIM_DETAILS_SCREEN);
    getAddressList();
  }, []);

  useEffect(() => {
    if (addresses.length > 0) {
      setNewDeliveryAddress(addresses[addresses.length - 1]);
    }
  }, [addresses]);

  // DRUG NAMES
  const getFreeDrugNames = () => {
    const visitInfo = nextCycle?.visit_meta_info;
    if (!visitInfo) return [];

    const { primary_drugs = [], secondary_drugs = [] } = visitInfo;

    const freeDrugs = [
      ...primary_drugs.filter(
        (drug: any) => drug.is_free === "1" || drug.is_free === 1
      ),
      ...secondary_drugs.filter(
        (drug: any) => drug.is_free === "1" || drug.is_free === 1
      ),
    ];

    // Return only drug names
    return freeDrugs.map((drug: any) => ({
      drug_label: drug.drug_label,
      cost: drug.cost,
    }));
  };

  const handleAddressChange = () => {
    navigate(`/app/${supportProgramKey}/claim-vivitra/add-complete-address`, {
      state: {
        nextCycle,
      },
    });
  };

  const handleClaimNow = () => {
    // Event: CLAIM_NOW_CLICKED
    analytics.trackEvent(EVENT_NAMES.CLAIM_FREE_DRUG_CLICKED, {
      screen_name: SCREEN_NAMES.CLAIM_DETAILS_SCREEN,
      support_program_key: supportProgramKey,
      card_title: "Claim Free Drug",
      ...newDeliveryAddress,
      ...nextCycle,
    });
    const sanitizedAddress = newDeliveryAddress?.address?.replace(/'/g, "");
    const payload = {
      patient_id: patient?.data?.patient_id,
      updated_by: patient?.data?.patient_id,
      patient_visit_id: nextCycle?.patient_visit_id,
      visit_meta_info: {
        visit_cycle: nextCycle?.visit_cycle,
        vivitra_claimed: 1,
        vivitra_address: sanitizedAddress,
        vivitra_approved: 0,
        vivitra_status: "Ordered",
        vivitra_date: JSON.stringify({
          ordered_date: moment().format("YYYY-MM-DD"),
        }),

        // NEW KEYS
        order_status: "Ordered",
        order_address: sanitizedAddress,
        is_free_drug_claimed: 1,
        order_date: JSON.stringify({
          ordered_date: moment().format("YYYY-MM-DD"),
        }),
      },
      visit_dates: [
        moment(nextCycle?.visit_date).format("YYYY-MM-DD")?.toString(),
      ],
    };

    // console.log("payload", payload);

    new Promise((resolve, reject) => {
      dispatch(
        journeyActions.updatePatientVisitRequest({
          resolve,
          reject,
          payload,
        })
      );
    })
      .then((res) => {
        // Event: CLAIM_FREE_DRUG_SUCCESS
        analytics.trackEvent(EVENT_NAMES.CLAIM_FREE_DRUG_SUCCESS, {
          screen_name: SCREEN_NAMES.CLAIM_DETAILS_SCREEN,
          support_program_key: supportProgramKey,
          card_title: "Claim Free Drug",
          ...newDeliveryAddress,
          ...nextCycle,
        });
        toast.success("Successfully Claimed");
        navigate(`/app/${supportProgramKey}/success`, {
          state: {
            success: true,
            successMessageTitle: "Claimed successfully",
            successMessageDescription:
              "Your have successfully claimed your free drugs for your next cycle",
            redirectUrl: `/app/${supportProgramKey}/home-screen`,
          },
        });
      })
      .catch((err) => {
        // Event: CLAIM_FREE_DRUG_ERROR
        analytics.trackEvent(EVENT_NAMES.CLAIM_FREE_DRUG_ERROR, {
          screen_name: SCREEN_NAMES.CLAIM_DETAILS_SCREEN,
          support_program_key: supportProgramKey,
          card_title: "Claim Free Drug",
          ...newDeliveryAddress,
          ...nextCycle,
        });
        console.log("err", err);
        toast.error(constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG);
      });
  };

  return (
    <Layout
      supportProgramKey={supportProgramKey || ""}
      header={
        <Header
          title="Claim Details"
          titlePosition="center"
          showBackButton={true}
          onBackClick={() => {
            navigate(-1);
          }}
        />
      }
      stickyHeader
    >
      <div className="relative w-full h-full bg-white flex flex-col justify-between gap-4">
        <div className="flex flex-col gap-4 p-4">
          <div className="w-full p-4 bg-gray-50 rounded-lg">
            <h5 className="text-base font-bold mb-1">
              {getOrdinal(nextCycle?.visit_cycle)} Cycle
            </h5>
            <div className="flex items-center gap-1 flex-wrap">
              <Calendar className="w-3 h-3 text-gray-500" />
              <p className="text-xs text-gray-500">
                Next cycle due on{" "}
                {moment(
                  nextCycle?.rescheduled_date || nextCycle?.visit_date
                ).format(DATE_FORMAT_STRING)}
              </p>
            </div>
          </div>
          <div className="w-full border-[var(--app-primary)] rounded-lg p-4 border bg-[var(--icon-bg-primary)]">
            <p className="text-[var(--text-primary)] text-sm mb-1">
              <span className="font-bold capitalize">
                {getFreeDrugNames()
                  .map((drug: any) => drug.drug_label)
                  .join(" + ")}
              </span>{" "}
              price for you
            </p>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <h4 className="text-[var(--text-primary)] text-2xl font-bold">
                  Free{" "}
                </h4>
                <span className="text-[var(--text-primary)] text-sm font-light line-through">
                  {`₹${getFreeDrugNames()
                    .map((drug: any) => drug.cost)
                    .reduce((acc, curr) => acc + curr, 0)}`}
                </span>
              </div>
            </div>
            <div className="w-full h-[1px] bg-[var(--app-primary)] my-2" />
            <p className="text-[var(--text-primary)] text-sm mb-1">
              Your doctor <br />
              <span className="font-bold capitalize">
                Dr. {patient?.data?.patient_meta_info?.doctor_name}
              </span>
            </p>
          </div>
          <div className="w-full p-4 bg-gray-50 rounded-lg">
            <div className="w-full text-sm border-b border-gray-500 pb-2 flex items-center gap-2 mb-1">
              <span className="block w-full">Delivery Address</span>
              <button
                type="button"
                className="text-[var(--app-primary)] p-1"
                onClick={handleAddressChange}
              >
                <SquarePen className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-xs text-gray-500 leading-tight">
                {newDeliveryAddress?.address}
              </p>
            </div>
          </div>
        </div>
        <div className="relative w-full border-t border-gray-200 p-4">
          <Button
            rounded="full"
            fullWidth
            disabled={!newDeliveryAddress}
            onClick={handleClaimNow}
          >
            Claim Now
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default ClaimDetailsScreen;
